import { normalizeTimer, type ActivityMode, type CompletedTimer, type CompletedTimerRevision, type RunningTimer, type TagDefinition } from "./domain";

export interface NudgePolicy {
  enabled: boolean;
  reviewAfterMinutes: number;
  quietStart: string;
  quietEnd: string;
  includeTaskName: boolean;
}

export interface NudgeEvent {
  id: string;
  timerId: string;
  reason: "overdue" | "returned" | "mode-contradiction";
  presentation: "inline" | "native" | "web";
  occurredAt: number;
  outcome?: "continued" | "finished" | "switched" | "snoozed";
}

export interface LocalLedger {
  version: 2;
  running: RunningTimer | null;
  completed: CompletedTimer[];
  tags: TagDefinition[];
  nudgePolicy: NudgePolicy;
  nudgeEvents: NudgeEvent[];
}

export interface DayTagDuration {
  tagId: string | null;
  milliseconds: number;
  live: boolean;
}

export interface DayTagBreakdown {
  totalMilliseconds: number;
  tags: DayTagDuration[];
}

export const defaultNudgePolicy: NudgePolicy = {
  enabled: true,
  reviewAfterMinutes: 60,
  quietStart: "22:00",
  quietEnd: "08:00",
  includeTaskName: false,
};

export const initialTags = (createdAt = Date.now()): TagDefinition[] => [
  { id: "tag-work", label: "Work", color: "#5b4bff", isFavorite: true, defaultActivityMode: "interactive_mac", createdAt },
  { id: "tag-dance", label: "Dance", color: "#e55c91", isFavorite: true, defaultActivityMode: "away_from_mac", createdAt },
  { id: "tag-family", label: "Family", color: "#ef8d3c", isFavorite: true, defaultActivityMode: "mixed", createdAt },
  { id: "tag-personal", label: "Personal", color: "#2f9b74", isFavorite: true, defaultActivityMode: "mixed", createdAt },
];

export function emptyLedger(now = Date.now()): LocalLedger {
  return {
    version: 2,
    running: null,
    completed: [],
    tags: initialTags(now),
    nudgePolicy: { ...defaultNudgePolicy },
    nudgeEvents: [],
  };
}

function validMode(value: unknown): value is ActivityMode {
  return ["interactive_mac", "passive_mac", "away_from_mac", "mixed"].includes(String(value));
}

function normalizedRevision(value: unknown): CompletedTimerRevision | null {
  if (!value || typeof value !== "object") return null;
  const revision = value as Partial<CompletedTimerRevision>;
  if (
    typeof revision.description !== "string" || !revision.description.trim() ||
    typeof revision.startedAt !== "number" || !Number.isSafeInteger(revision.startedAt) || revision.startedAt < 0 ||
    typeof revision.endedAt !== "number" || !Number.isSafeInteger(revision.endedAt) || revision.endedAt < revision.startedAt ||
    !Array.isArray(revision.tagIds) || !validMode(revision.activityMode) ||
    typeof revision.supersededAt !== "number" || !Number.isSafeInteger(revision.supersededAt) || revision.supersededAt < 0
  ) return null;
  return {
    description: revision.description.trim().replace(/\s+/g, " ").slice(0, 180),
    startedAt: revision.startedAt,
    endedAt: revision.endedAt,
    tagIds: [...new Set(revision.tagIds.filter((tagId): tagId is string => typeof tagId === "string" && Boolean(tagId.trim())).map((tagId) => tagId.trim()))],
    activityMode: revision.activityMode,
    supersededAt: revision.supersededAt,
  };
}

function validTag(value: unknown): value is TagDefinition {
  if (!value || typeof value !== "object") return false;
  const tag = value as Partial<TagDefinition>;
  return typeof tag.id === "string" && typeof tag.label === "string" && /^#[0-9a-f]{6}$/i.test(tag.color ?? "") && validMode(tag.defaultActivityMode);
}

function normalizedPolicy(value: unknown): NudgePolicy {
  const policy = value && typeof value === "object" ? value as Partial<NudgePolicy> : {};
  return {
    enabled: typeof policy.enabled === "boolean" ? policy.enabled : defaultNudgePolicy.enabled,
    reviewAfterMinutes: typeof policy.reviewAfterMinutes === "number"
      ? Math.min(240, Math.max(15, Math.round(policy.reviewAfterMinutes)))
      : defaultNudgePolicy.reviewAfterMinutes,
    quietStart: /^\d{2}:\d{2}$/.test(policy.quietStart ?? "") ? policy.quietStart! : defaultNudgePolicy.quietStart,
    quietEnd: /^\d{2}:\d{2}$/.test(policy.quietEnd ?? "") ? policy.quietEnd! : defaultNudgePolicy.quietEnd,
    includeTaskName: typeof policy.includeTaskName === "boolean" ? policy.includeTaskName : defaultNudgePolicy.includeTaskName,
  };
}

export function migrateLedger(value: unknown, now = Date.now()): LocalLedger {
  if (!value || typeof value !== "object") return emptyLedger(now);
  const candidate = value as {
    version?: unknown;
    running?: unknown;
    completed?: unknown;
    tags?: unknown;
    nudgePolicy?: unknown;
    nudgeEvents?: unknown;
  };
  const normalizeRunning = (timer: unknown): RunningTimer | null => {
    if (!timer || typeof timer !== "object") return null;
    const item = timer as Partial<RunningTimer>;
    if (typeof item.id !== "string" || typeof item.description !== "string" || typeof item.startedAt !== "number") return null;
    return normalizeTimer(item as RunningTimer);
  };
  const completed = Array.isArray(candidate.completed)
    ? candidate.completed.flatMap((timer) => {
        const running = normalizeRunning(timer);
        const endedAt = (timer as Partial<CompletedTimer> | null)?.endedAt;
        if (!running || typeof endedAt !== "number" || endedAt < running.startedAt) return [];
        const revisions = Array.isArray((timer as Partial<CompletedTimer>).revisions)
          ? (timer as Partial<CompletedTimer>).revisions!.map(normalizedRevision).filter((revision): revision is CompletedTimerRevision => Boolean(revision)).slice(-20)
          : [];
        return [{ ...running, endedAt, ...(revisions.length ? { revisions } : {}) }];
      })
    : [];
  const storedTags = Array.isArray(candidate.tags) ? candidate.tags.filter(validTag) : [];
  const tags = storedTags.length ? storedTags.map((tag) => ({ ...tag, color: tag.color.toLowerCase() })) : initialTags(now);
  return {
    version: 2,
    running: normalizeRunning(candidate.running),
    completed,
    tags,
    nudgePolicy: normalizedPolicy(candidate.nudgePolicy),
    nudgeEvents: candidate.version === 2 && Array.isArray(candidate.nudgeEvents)
      ? candidate.nudgeEvents.filter((event): event is NudgeEvent => Boolean(event && typeof event === "object" && typeof (event as NudgeEvent).id === "string")).slice(-500)
      : [],
  };
}

export function activityModeForTags(tagIds: string[], tags: TagDefinition[]): ActivityMode {
  const modes = [...new Set(tagIds.map((id) => tags.find((tag) => tag.id === id && !tag.archivedAt)?.defaultActivityMode).filter(Boolean))] as ActivityMode[];
  return modes.length === 1 ? modes[0]! : "mixed";
}

interface TimeInterval { start: number; end: number }

function unionedDuration(intervals: TimeInterval[]): number {
  const ordered = intervals.filter((interval) => interval.end > interval.start).sort((a, b) => a.start - b.start || a.end - b.end);
  if (!ordered.length) return 0;
  let total = 0;
  let start = ordered[0]!.start;
  let end = ordered[0]!.end;
  for (const interval of ordered.slice(1)) {
    if (interval.start <= end) {
      end = Math.max(end, interval.end);
      continue;
    }
    total += end - start;
    start = interval.start;
    end = interval.end;
  }
  return total + end - start;
}

export function dayTagBreakdown(ledger: LocalLedger, dayStart: number, asOf: number): DayTagBreakdown {
  if (asOf <= dayStart) return { totalMilliseconds: 0, tags: [] };
  const all: TimeInterval[] = [];
  const byTag = new Map<string | null, TimeInterval[]>();
  const live = new Set<string | null>();
  const include = (startedAt: number, endedAt: number, tagIds: string[], isLive = false): void => {
    const interval = { start: Math.max(dayStart, startedAt), end: Math.min(asOf, endedAt) };
    if (interval.end <= interval.start) return;
    all.push(interval);
    const keys = tagIds.length ? [...new Set(tagIds)] : [null];
    for (const key of keys) {
      const intervals = byTag.get(key) ?? [];
      intervals.push(interval);
      byTag.set(key, intervals);
      if (isLive) live.add(key);
    }
  };

  for (const entry of ledger.completed) include(entry.startedAt, entry.endedAt, entry.tagIds);
  if (ledger.running) include(ledger.running.startedAt, asOf, ledger.running.tagIds, true);

  const tags = [...byTag.entries()]
    .map(([tagId, intervals]) => ({ tagId, milliseconds: unionedDuration(intervals), live: live.has(tagId) }))
    .filter((entry) => entry.milliseconds > 0)
    .sort((a, b) => b.milliseconds - a.milliseconds || String(a.tagId).localeCompare(String(b.tagId)));
  return { totalMilliseconds: unionedDuration(all), tags };
}

export function isQuietHour(policy: NudgePolicy, date = new Date()): boolean {
  const current = date.getHours() * 60 + date.getMinutes();
  const minutes = (value: string): number => {
    const [hour = 0, minute = 0] = value.split(":").map(Number);
    return hour * 60 + minute;
  };
  const start = minutes(policy.quietStart);
  const end = minutes(policy.quietEnd);
  if (start === end) return false;
  return start < end ? current >= start && current < end : current >= start || current < end;
}
